import { useRecoilState, useRecoilValue } from "recoil";
import { dspProductAtom, productKeyAtom } from "src/atoms";
import { isTest, productInfo, ProductKey } from "src/config/config";
import mindSatIcon from "src/assets/images/mind_sat_icon.svg";
import wrapsodyIcon from "src/assets/images/wrapsody_icon.svg";
import ecoIcon from "src/assets/images/eco_icon.svg";
import firesideIcon from "src/assets/images/fireside_icon.svg";
import dspmIcon from "src/assets/images/dspm_icon.svg";
import fedIcon from "src/assets/images/fed_icon.svg";
import fssIcon from "src/assets/images/fss_icon.svg";
import fspIcon from "src/assets/images/fsp_icon.svg";
import fcBrIcon from "src/assets/images/fcbr_icon.svg";
import dataRadarIcon from "src/assets/images/data_radar_icon.svg";
import { BadgeType } from "components/Common/ProductBadge/ProductBadge";
import { useTranslation } from "react-i18next";
import { useRouter } from "src/hooks/useRouter";

// 제품별 정보 타입 정의
interface ProductCardInfo {
  name: string;
  subtitle: string;
  description: string[];
  features: string[];
  isRequired: boolean;
  word?: string; // 제품 한줄 소개
  moreInfoUrl?: string;
  startNowUrl?: string;
  iconSrc?: string;
  tags?: Array<{
    text: string;
    variant: "required" | "optional" | "new" | "beta";
  }>;
}

// 카테고리 타입 정의
export type CategoryType =
  | "all"
  | "data-security"
  | "cloud-security"
  | "privacy-detection"
  | "phishing-training"
  | "document-management"
  | "supply-chain-security"
  | "secure-messenger";

interface IMoreInfoUrl {
  type: "default" | "primary";
  label: string;
  url: string;
}
// 제품 타입 정의
export interface ProductType {
  id: string;
  name: string; // 표기될 제품 이름
  // productKey?: ProductKey;
  productKey?: string;
  icon: string; // 아이콘 이미지
  badge?: BadgeType[]; // 배지 타입
  categories: CategoryType[]; // 카테고리
  description: string; // 간단한 설명
  word: string; // 제품 한줄 소개
  moreInfo: {
    description: string[]; // 자세한 설명
    subTitle: string; // 자세한 설명 제목
    features: string[]; // 주요 기능
  };
  moreInfoUrl?: IMoreInfoUrl[]; // 추가 버튼 목록
}
// 제품 슬러그 매핑
export const productSlugMap: Record<string, string> = {
  wrapsody: "WRAPSODY",
  wrapsody_eco: "WRAPSODY_ECO",
  fireside: "FIRESIDE",
  dspm: "DSPM",
  mind_sat: "MIND_SAT",
  fss: "FSS",
  fsp: "FSP",
  fc_br: "FC_BR",
  fed_n: "FED",
  fdr: "FDR",
};
export const productNameMap: Record<string, string> = {
  WRAPSODY: "Wrapsody Cloud",
  WRAPSODY_ECO: "Wrapsody eCo Cloud",
  MIND_SAT: "Mind-SAT",
  FED: "FED-N",
  FC_BR: "FC_BR",
  FSP: "FSP",
  FSS: "FSS",
  FDR: "FDR",
  DSPM: "DSPM",
  FIRESIDE: "Fireside",
};
// DSP 제품 리스트
export const isDspProductList = ["FED", "FSS", "FSP", "FDR", "DSP", "FC_BR"];
export type DspProductListType =
  | "FED"
  | "FSS"
  | "FSP"
  | "FDR"
  | "FC_BR"
  | "DSP";
interface ProductOption {
  label: string;
  value: string;
  icon: string;
  description: string;
}
const useProductInfo = (): {
  product: ProductType;
  cardInfo: ProductType["moreInfo"];
  products: ProductType[];
  productOptions: ProductOption[];
  productName: string;
  getProductInfo: (productKey: ProductKey) => ProductType;
  getFeatureCount: () => number;
} => {
  const [productKey] = useRecoilState(productKeyAtom);
  const [dspProduct] = useRecoilState(dspProductAtom);
  const { lang } = useRouter();
  const { t } = useTranslation();
  const isKorean = lang === "ko";
  const productsInfoMap: Record<string, ProductType> = {
    MIND_SAT: {
      id: "mind-sat",
      name: "Mind-SAT",
      productKey: "MIND_SAT",
      icon: mindSatIcon,
      badge: [],
      categories: ["phishing-training"],
      word: t("home.productInfo.MIND_SAT.word"),
      description: t("home.productInfo.MIND_SAT.description"),
      moreInfo: {
        description: [
          t("home.productInfo.MIND_SAT.moreInfo.description.1"),
          t("home.productInfo.MIND_SAT.moreInfo.description.2"),
          t("home.productInfo.MIND_SAT.moreInfo.description.3"),
        ],
        subTitle: t("home.productInfo.MIND_SAT.moreInfo.description.subTitle"),
        features: [
          t("home.productInfo.MIND_SAT.moreInfo.features.1"),
          t("home.productInfo.MIND_SAT.moreInfo.features.2"),
          t("home.productInfo.MIND_SAT.moreInfo.features.3"),
        ],
      },
      moreInfoUrl: [
        {
          type: "default",
          label: t("home.productInfo.btn.learnMore"),
          url: "https://www.mind-sat.com",
        },
      ],
    },
    WRAPSODY: {
      id: "wrapsody",
      name: "Wrapsody",
      productKey: "WRAPSODY",
      icon: wrapsodyIcon,
      badge: [],
      categories: ["document-management"],
      word: t("home.productInfo.WRAPSODY.word"),
      description: t("home.productInfo.WRAPSODY.description"),
      moreInfo: {
        description: [
          t("home.productInfo.WRAPSODY.moreInfo.description.1"),
          t("home.productInfo.WRAPSODY.moreInfo.description.2"),
          t("home.productInfo.WRAPSODY.moreInfo.description.3"),
        ],
        subTitle: t("home.productInfo.WRAPSODY.description"),
        features: [
          t("home.productInfo.WRAPSODY.moreInfo.features.1"),
          t("home.productInfo.WRAPSODY.moreInfo.features.2"),
          t("home.productInfo.WRAPSODY.moreInfo.features.3"),
        ],
      },
      moreInfoUrl: [
        {
          type: "default",
          label: t("home.productInfo.btn.learnMore"),
          url: "https://www.wrapsody.com",
        },
        {
          type: "default",
          label: t("home.productInfo.btn.startNow"),
          url: "https://www.wrapsody.com/kr/start",
        },
      ],
    },
    WRAPSODY_ECO: {
      id: "wrapsody-eco",
      productKey: "WRAPSODY_ECO",
      icon: ecoIcon,
      badge: [],
      categories: ["supply-chain-security"],
      name: "Wrapsody eCo",
      word: t("home.productInfo.WRAPSODY_ECO.word"),
      description: t("home.productInfo.WRAPSODY_ECO.description"),
      moreInfo: {
        description: [
          t("home.productInfo.WRAPSODY_ECO.moreInfo.description.1"),
          t("home.productInfo.WRAPSODY_ECO.moreInfo.description.2"),
          t("home.productInfo.WRAPSODY_ECO.moreInfo.description.3"),
        ],
        subTitle: t("home.productInfo.WRAPSODY_ECO.description"),
        features: [
          t("home.productInfo.WRAPSODY_ECO.moreInfo.features.1"),
          t("home.productInfo.WRAPSODY_ECO.moreInfo.features.2"),
          t("home.productInfo.WRAPSODY_ECO.moreInfo.features.3"),
        ],
      },
      moreInfoUrl: [
        {
          type: "default",
          label: t("home.productInfo.btn.learnMore"),
          url: "https://www.wrapsodyeco.com",
        },
        {
          type: "default",
          label: t("home.productInfo.btn.startFree"),
          url: "https://www.wrapsodyeco.com/kr/start-free",
        },
      ],
    },
    FED: {
      id: "fed",
      productKey: "FED",
      icon: fedIcon,
      badge: [],
      categories: ["data-security"],
      name: t("home.productInfo.FED.name"),
      word: t("home.productInfo.FED.word"),
      description: t("home.productInfo.FED.description"),
      moreInfo: {
        description: [
          t("home.productInfo.FED.moreInfo.description.1"),
          t("home.productInfo.FED.moreInfo.description.2"),
          t("home.productInfo.FED.moreInfo.description.3"),
          t("home.productInfo.FED.moreInfo.description.4"),
        ],
        subTitle: t("home.productInfo.FED.description"),
        features: [
          t("home.productInfo.FED.moreInfo.features.1"),
          t("home.productInfo.FED.moreInfo.features.2"),
          t("home.productInfo.FED.moreInfo.features.3"),
        ],
      },
      moreInfoUrl: [
        {
          type: "default",
          label: t("home.productInfo.btn.learnMore"),
          url: "https://www.fasoo.com/products/fasoo-enterprise-drm-for-node",
        },
      ],
    },
    FC_BR: {
      id: "fcbr",
      productKey: "FC_BR",
      icon: fcBrIcon,
      badge: [],
      categories: ["data-security"],
      name: t("home.productInfo.FC_BR.name"),
      word: t("home.productInfo.FC_BR.word"),
      description: t("home.productInfo.FC_BR.description"),
      moreInfo: {
        subTitle: t("home.productInfo.FC_BR.description"),
        description: [
          t("home.productInfo.FC_BR.moreInfo.description.1"),
          t("home.productInfo.FC_BR.moreInfo.description.2"),
          t("home.productInfo.FC_BR.moreInfo.description.3"),
        ],
        features: [
          t("home.productInfo.FC_BR.moreInfo.features.1"),
          t("home.productInfo.FC_BR.moreInfo.features.2"),
          t("home.productInfo.FC_BR.moreInfo.features.3"),
        ],
      },
      moreInfoUrl: [
        {
          type: "default",
          label: t("home.productInfo.btn.learnMore"),
          url: "https://www.fasoo.com/products/fasoo-content-backup-and-recovery",
        },
      ],
    },
    FSP: {
      id: "fsp",
      productKey: "FSP",
      icon: fspIcon,
      categories: ["data-security"],
      name: t("home.productInfo.FSP.name"),
      description: t("home.productInfo.FSP.description"),
      word: t("home.productInfo.FSP.word"),
      moreInfo: {
        description: [
          t("home.productInfo.FSP.moreInfo.description.1"),
          t("home.productInfo.FSP.moreInfo.description.2"),
          t("home.productInfo.FSP.moreInfo.description.3"),
        ],
        subTitle: t("home.productInfo.FSP.description"),
        features: [
          t("home.productInfo.FSP.moreInfo.features.1"),
          t("home.productInfo.FSP.moreInfo.features.2"),
          t("home.productInfo.FSP.moreInfo.features.3"),
        ],
      },
      moreInfoUrl: [
        {
          type: "default",
          label: t("home.productInfo.btn.learnMore"),
          url: "https://www.fasoo.com/products/fasoo-smart-print",
        },
      ],
    },
    FSS: {
      id: "fss",
      productKey: "FSS",
      icon: fssIcon,
      badge: [],
      categories: ["data-security"],
      name: t("home.productInfo.FSS.name"),
      description: t("home.productInfo.FSS.description"),
      word: t("home.productInfo.FSS.word"),
      moreInfo: {
        description: [
          t("home.productInfo.FSS.moreInfo.description.1"),
          t("home.productInfo.FSS.moreInfo.description.2"),
          t("home.productInfo.FSS.moreInfo.description.3"),
        ],
        subTitle: t("home.productInfo.FSS.description"),
        features: [
          t("home.productInfo.FSS.moreInfo.features.1"),
          t("home.productInfo.FSS.moreInfo.features.2"),
          t("home.productInfo.FSS.moreInfo.features.3"),
        ],
      },
      moreInfoUrl: [
        {
          type: "default",
          label: t("home.productInfo.btn.learnMore"),
          url: "https://www.fasoo.com/products/fasoo-smart-screen",
        },
      ],
    },
    FDR: {
      id: "fdr",
      productKey: "FDR",
      icon: dataRadarIcon,
      badge: [],
      categories: ["privacy-detection", "data-security"],
      name: t("home.productInfo.FDR.name"),
      description: t("home.productInfo.FDR.description"),
      word: t("home.productInfo.FDR.word"),
      moreInfo: {
        subTitle: t("home.productInfo.FDR.description"),
        description: [
          t("home.productInfo.FDR.moreInfo.description.1"),
          t("home.productInfo.FDR.moreInfo.description.2"),
          t("home.productInfo.FDR.moreInfo.description.3"),
        ],
        features: [
          t("home.productInfo.FDR.moreInfo.features.1"),
          t("home.productInfo.FDR.moreInfo.features.2"),
          t("home.productInfo.FDR.moreInfo.features.3"),
        ],
      },
      moreInfoUrl: [
        {
          type: "default",
          label: t("home.productInfo.btn.learnMore"),
          url: "https://www.fasoo.com/products/fasoo-data-radar",
        },
      ],
    },
    DSPM: {
      id: "dspm",
      productKey: "DSPM",
      icon: dspmIcon,
      badge: [],
      categories: ["cloud-security"],
      name: t("home.productInfo.DSPM.name"),
      description: t("home.productInfo.DSPM.description"),
      word: t("home.productInfo.DSPM.word"),
      moreInfo: {
        description: [
          t("home.productInfo.DSPM.moreInfo.description.1"),
          t("home.productInfo.DSPM.moreInfo.description.2"),
          t("home.productInfo.DSPM.moreInfo.description.3"),
        ],
        subTitle: t("home.productInfo.DSPM.description"),
        features: [
          t("home.productInfo.DSPM.moreInfo.features.1"),
          t("home.productInfo.DSPM.moreInfo.features.2"),
          t("home.productInfo.DSPM.moreInfo.features.3"),
        ],
      },
      moreInfoUrl: [
        {
          type: "default",
          label: t("home.productInfo.btn.learnMore"),
          url: "https://dspm.fasoo.com/",
        },
      ],
    },
    FIRESIDE: {
      id: "fireside",
      productKey: "FIRESIDE",
      icon: firesideIcon,
      badge: [],
      categories: ["secure-messenger"],
      name: t("home.productInfo.FIRESIDE.name"),
      description: t("home.productInfo.FIRESIDE.description"),
      word: t("home.productInfo.FIRESIDE.word"),
      moreInfo: {
        description: [
          t("home.productInfo.FIRESIDE.moreInfo.description.1"),
          t("home.productInfo.FIRESIDE.moreInfo.description.2"),
          t("home.productInfo.FIRESIDE.moreInfo.description.3"),
        ],
        subTitle: t("home.productInfo.FIRESIDE.description"),
        features: [
          t("home.productInfo.FIRESIDE.moreInfo.features.1"),
          t("home.productInfo.FIRESIDE.moreInfo.features.2"),
          t("home.productInfo.FIRESIDE.moreInfo.features.3"),
        ],
      },
      moreInfoUrl: [
        {
          type: "default",
          label: t("home.productInfo.btn.learnMore"),
          url: "https://fireside.fasoo.com/",
        },
      ],
    },
  };
  // if (!isTest) {
  // delete productsInfoMap["MIND_SAT"];
  // delete productsInfoMap["FIRESIDE"];
  // }

  // 기본 제품 정보 가져오기
  // const baseProductInfo = productInfo[productKey];
  const realProduct = productKey === "DSP" ? dspProduct : productKey;
  // const realProduct = productKey;
  // 확장된 제품 카드 정보 가져오기
  const product = productsInfoMap[realProduct as ProductKey];
  const cardInfo = productsInfoMap[realProduct as ProductKey]?.moreInfo;
  const products: ProductType[] = Object.values(productsInfoMap);
  const productOptions = Object.values(productsInfoMap).map((product) => ({
    label: product.name || "",
    value: product.productKey || "",
    icon: product.icon || "",
    description: product.description || "",
  }));
  const productName = product?.productKey
    ? isDspProductList.includes(product?.productKey)
      ? "Fasoo DSP"
      : product?.name
    : "";
  // console.log("products", products);
  // 제품 정보가 없는 경우 기본값 반환
  // if (!baseProductInfo || !cardInfo) {
  //   return {
  //     product: {} as ProductType,
  //     cardInfo: {} as ProductType["moreInfo"],
  //     getFeatureCount: () => 0,
  //   };
  // }

  return {
    product,
    cardInfo,
    products,
    productOptions,
    productName,
    // 유틸리티 함수들
    getProductInfo: (productKey: ProductKey) => productsInfoMap[productKey],
    getFeatureCount: () => cardInfo.features.length,
  };
};

export default useProductInfo;

// 타입 export
export type { ProductCardInfo };
